import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { useToast } from '../components/Toast';

const TYPE_ICON = { task: '📋', ticket: '🎫', onboarding: '👋', offboarding: '🚪', system: '🔔' };

const timeAgo = (date) => {
  const diff = Math.floor((new Date() - new Date(date)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const Notifications = () => {
  const [notifs, setNotifs] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const ref = React.useRef(null);

  const loadCount = async () => {
    try {
      const { data } = await api.get('/notifications/count');
      setCount(data.count);
    } catch {
      /* badge is non-critical — stay silent */
    }
  };

  /**
   * Previously this swallowed errors, so a failed request looked exactly like
   * "no notifications" while the badge still showed a count. Surface it instead.
   */
  const loadNotifs = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get('/notifications');
      setNotifs(Array.isArray(data) ? data : []);
    } catch (err) {
      setLoadError(err.response?.data?.message || err.message || 'Could not load notifications');
      setNotifs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCount();
    const interval = setInterval(loadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) {
      loadNotifs();
    }
  }, [open]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markAllRead = async () => {
    await api.patch('/notifications/read-all');
    setCount(0);
    setNotifs((n) => n.map((x) => ({ ...x, is_read: 1 })));
  };

  return (
    <div ref={ref} className="topbar-notif-wrap">
      <button
        type="button"
        className="topbar-icon-btn"
        onClick={() => setOpen(!open)}
        title="Notifications"
      >
        <i className="ti ti-bell"></i>
        {count > 0 && (
          <span className="notif-badge">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="topbar-notif-panel">
          <div
            style={{
              padding: '14px 16px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
              Notifications
            </span>
            {count > 0 && (
              <span
                style={{
                  marginLeft: 8,
                  background: 'var(--red-bg)',
                  color: 'var(--red)',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '1px 8px',
                  borderRadius: 10,
                }}
              >
                {count} unread
              </span>
            )}
            {count > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  marginLeft: 'auto',
                  fontSize: 11,
                  color: 'var(--blue)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontFamily: 'var(--font)',
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                Loading…
              </div>
            ) : loadError ? (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--red)', fontSize: 13, lineHeight: 1.5 }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
                {loadError}
                <div style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={loadNotifs}
                    style={{
                      fontSize: 12, color: 'var(--blue)', background: 'none',
                      border: 'none', cursor: 'pointer', fontFamily: 'var(--font)',
                    }}
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : notifs.length === 0 ? (
              <div
                style={{
                  padding: 32,
                  textAlign: 'center',
                  color: 'var(--text3)',
                  fontSize: 13,
                }}
              >
                <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
                {count > 0
                  ? `${count} unread, but none could be loaded — try Retry`
                  : 'No notifications yet'}
              </div>
            ) : (
              notifs.map((n) => (
                <div
                  key={n.id}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #f1f3f4',
                    background: n.is_read ? '#fff' : '#f8fbff',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8f9fa';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = n.is_read ? '#fff' : '#f8fbff';
                  }}
                >
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 20, flexShrink: 0 }}>
                      {TYPE_ICON[n.type] || '🔔'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: n.is_read ? 400 : 600,
                          color: 'var(--text)',
                          marginBottom: 2,
                        }}
                      >
                        {n.title}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>
                        {n.message}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                        {timeAgo(n.created_at)}
                      </div>
                    </div>
                    {!n.is_read && (
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: 'var(--blue)',
                          flexShrink: 0,
                          marginTop: 4,
                        }}
                      />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
